import React, { useEffect, useState, useRef, useCallback } from 'react';
import './AddressAutocomplete.css';
import { searchAddresses } from '../../services/geocoding';

// Address search via our own /api/geocode proxy, which uses Mapbox and falls
// back to Photon (OpenStreetMap) when Mapbox is unreachable. This component
// deliberately knows nothing about either provider.
//
// onSelect receives { address, lat, lng, typed? }
// - typed: true when the user is editing the text (lat/lng cleared)
// - typed: undefined when picked from a suggestion (lat/lng populated)
//
// Soft geographic bias — approximately the centre of Australia. Results near
// this point rank higher without anywhere being excluded.
const AU_BIAS_LAT = -25.27;
const AU_BIAS_LON = 133.78;

function AddressAutocomplete({
  value,
  onSelect,
  placeholder,
  inputClassName = '',
  errorClassName = '',
  filledClassName = '',
  hasError = false,
  biasLat = AU_BIAS_LAT,
  biasLon = AU_BIAS_LON,
  inputStyle,
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  // In-flight suggestion request, so a slow answer for "syd" cannot land after
  // "sydney" and repopulate the list with stale results.
  const abortRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Drop any pending timer and request when the field goes away mid-search.
  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const results = await searchAddresses(query, {
      biasLat,
      biasLon,
      signal: controller.signal,
    });
    // A superseded request must not touch state — the newer one owns it now.
    if (controller.signal.aborted) return;
    setLoading(false);
    setSuggestions(results);
    setOpen(results.length > 0);
  }, [biasLat, biasLon]);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onSelect({ address: val, lat: null, lng: null, typed: true });
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (suggestion) => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    setInputValue(suggestion.label);
    setSuggestions([]);
    setOpen(false);
    setLoading(false);
    // Suggestions arrive with their coordinates already attached, so this is a
    // single update — no intermediate state where the address is set but the
    // coordinates are still null. That matters: callers read a null coordinate
    // as a cleared field and wipe an auto-calculated distance on the strength
    // of it (see Step4AssemblyDistribution's handleAddressSelect).
    onSelect({ address: suggestion.label, lat: suggestion.lat, lng: suggestion.lng });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') setOpen(false);
  };

  const composedClassName = [
    inputClassName,
    hasError ? errorClassName : '',
    inputValue ? filledClassName : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="address-autocomplete" ref={containerRef}>
      <input
        type="text"
        className={composedClassName}
        style={inputStyle}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Type a city, port or address...'}
        autoComplete="off"
      />
      {loading && <div className="address-loading">Searching...</div>}
      {open && suggestions.length > 0 && (
        <ul className="address-suggestions" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={`${s.lat},${s.lng},${i}`}
              className="address-suggestion-item"
              onMouseDown={() => handleSelect(s)}
              role="option"
              aria-selected={false}
            >
              <span className="address-suggestion-label">{s.label}</span>
              {s.kind && <span className="address-suggestion-kind">{s.kind}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
